import { getApiClient } from "../http/client";
import type { DataResponse } from "../http/types";
import type { PageParams } from "../common";
import type { RegionInfo, Site, SiteStatus, SiteUpsertRequest } from "./types";

interface ListSitesParams extends PageParams {
  search?: string;
  status?: SiteStatus;
}

export interface SiteListResult {
  content: Site[];
  total: number;
}

function normalizeSiteList(raw: unknown): SiteListResult {
  const page1 = (raw as { data?: { content?: Site[]; totalElements?: number } })?.data;
  if (page1?.content) {
    return { content: page1.content, total: page1.totalElements ?? page1.content.length };
  }
  const page2 = raw as { content?: Site[]; totalElements?: number };
  if (page2?.content) {
    return { content: page2.content, total: page2.totalElements ?? page2.content.length };
  }
  if (Array.isArray(raw)) {
    return { content: raw as Site[], total: (raw as Site[]).length };
  }
  return { content: [], total: 0 };
}

export const siteService = {
  list: async (params?: ListSitesParams): Promise<SiteListResult> => {
    const res = await getApiClient().get<unknown>("/sites", {
      params: params as Record<string, string | number | boolean | undefined>,
    });
    return normalizeSiteList(res);
  },

  get: async (id: number): Promise<Site> => {
    const res = (await getApiClient().get<DataResponse<Site>>(`/sites/${id}`)) as unknown;
    const wrapped = (res as { data?: Site })?.data;
    return wrapped ?? (res as Site);
  },

  create: async (body: SiteUpsertRequest): Promise<number> => {
    return getApiClient().post<number>(`/sites`, body);
  },

  update: async (id: number, body: SiteUpsertRequest): Promise<void> => {
    await getApiClient().put<void>(`/sites/${id}`, body);
  },

  remove: async (id: number): Promise<void> => {
    await getApiClient().delete<void>(`/sites/${id}`);
  },

  regions: async (): Promise<RegionInfo[]> => {
    const res = (await getApiClient().get<DataResponse<RegionInfo[]>>(`/sites/regions`)) as unknown;
    const wrapped = (res as { data?: RegionInfo[] })?.data;
    return Array.isArray(wrapped) ? wrapped : Array.isArray(res) ? (res as RegionInfo[]) : [];
  },
};
